// src/components/projects/ProjectDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { projectApi } from '../../api/projectApi';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../common/LoadingSpinner';
import StatusBadge from '../common/StatusBadge';
import { formatCurrency, formatDate } from '../../utils/formatters';
import {
    PencilSquareIcon, ArrowLeftIcon, MapPinIcon,
    CalendarIcon, UserIcon, CurrencyDollarIcon,
    ClipboardDocumentListIcon, DocumentTextIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const TASK_STATUS_COLORS = {
    TODO: 'bg-gray-100 text-gray-600',
    IN_PROGRESS: 'bg-blue-100 text-blue-700',
    BLOCKED: 'bg-red-100 text-red-700',
    COMPLETED: 'bg-emerald-100 text-emerald-700',
};

const TASK_STATUS_LABELS = {
    TODO: 'Da fare', IN_PROGRESS: 'In corso',
    BLOCKED: 'Bloccato', COMPLETED: 'Completato'
};

const CATEGORY_LABELS = {
    MATERIALS: 'Materiali', EQUIPMENT: 'Attrezzature', LABOR: 'Manodopera',
    FUEL: 'Carburante', SUBCONTRACT: 'Subappalti', TRANSPORT: 'Trasporti',
    PERMITS: 'Permessi', ADMIN: 'Amministrative', OTHER: 'Altro'
};

const TabButton = ({ active, onClick, children, count }) => (
    <button
        onClick={onClick}
        className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${active
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
    >
        {children}
        {count !== undefined && (
            <span className={`px-1.5 py-0.5 rounded-full text-xs ${active ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'}`}>
                {count}
            </span>
        )}
    </button>
);

const ProjectDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');

    useEffect(() => {
        loadProject();
    }, [id]);

    const loadProject = async () => {
        try {
            setLoading(true);
            const response = await projectApi.getById(id);
            setProject(response.data);
        } catch (err) {
            toast.error('Errore nel caricamento del progetto');
            navigate('/projects');
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <LoadingSpinner />;
    if (!project) return null;

    const totalExpenses = project.expenses?.reduce((s, e) => s + e.totalAmount, 0) || 0;
    const utilization = project.estimatedBudget > 0
        ? ((totalExpenses / project.estimatedBudget) * 100)
        : 0;
    const budgetBarColor = utilization >= 90 ? 'bg-red-500' : utilization >= 70 ? 'bg-yellow-400' : 'bg-emerald-500';
    const completedTasks = project.phases?.flatMap(p => p.tasks || []).filter(t => t.status === 'COMPLETED').length || 0;
    const totalTasks = project.phases?.flatMap(p => p.tasks || []).length || 0;

    return (
        <div className="space-y-6">

            {/* Header */}
            <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                    <button
                        onClick={() => navigate('/projects')}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition mt-0.5"
                    >
                        <ArrowLeftIcon className="w-5 h-5" />
                    </button>
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <StatusBadge status={project.status} />
                            <span className="text-xs font-mono text-gray-400 bg-gray-100 px-2 py-0.5 rounded-md">{project.code}</span>
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900">{project.name}</h1>
                        {project.description && (
                            <p className="text-gray-500 text-sm mt-1">{project.description}</p>
                        )}
                    </div>
                </div>

                {['SUPER_ADMIN', 'SITE_MANAGER'].includes(user?.role) && (
                    <Link
                        to={`/projects/${id}/edit`}
                        className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2.5 rounded-xl hover:bg-blue-700 transition font-medium text-sm flex-shrink-0"
                    >
                        <PencilSquareIcon className="w-4 h-4" />
                        Modifica
                    </Link>
                )}
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-2xl border p-5">
                    <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Budget</p>
                    <p className="text-xl font-bold text-gray-900 mt-1">{formatCurrency(project.estimatedBudget)}</p>
                    <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full ${budgetBarColor} rounded-full`} style={{ width: `${Math.min(utilization, 100)}%` }} />
                    </div>
                    <p className="text-xs text-gray-400 mt-1">{utilization.toFixed(1)}% utilizzato</p>
                </div>
                <div className="bg-white rounded-2xl border p-5">
                    <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Spese</p>
                    <p className="text-xl font-bold text-gray-900 mt-1">{formatCurrency(totalExpenses)}</p>
                    <p className="text-xs text-gray-400 mt-1">{project.expenses?.length || 0} transazioni</p>
                </div>
                <div className="bg-white rounded-2xl border p-5">
                    <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Task</p>
                    <p className="text-xl font-bold text-gray-900 mt-1">{completedTasks}/{totalTasks}</p>
                    <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-500 rounded-full" style={{ width: totalTasks ? `${(completedTasks / totalTasks) * 100}%` : '0%' }} />
                    </div>
                </div>
                <div className="bg-white rounded-2xl border p-5">
                    <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Progresso</p>
                    <p className="text-xl font-bold text-gray-900 mt-1">{project.progress || 0}%</p>
                    <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-purple-500 rounded-full" style={{ width: `${project.progress || 0}%` }} />
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-2xl border overflow-hidden">
                <div className="flex border-b overflow-x-auto">
                    <TabButton active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
                        📊 Overview
                    </TabButton>
                    <TabButton active={activeTab === 'phases'} onClick={() => setActiveTab('phases')} count={project.phases?.length}>
                        📋 Fasi e Task
                    </TabButton>
                    <TabButton active={activeTab === 'expenses'} onClick={() => setActiveTab('expenses')} count={project.expenses?.length}>
                        💰 Spese
                    </TabButton>
                    <TabButton active={activeTab === 'documents'} onClick={() => setActiveTab('documents')} count={project.documents?.length}>
                        📄 Documenti
                    </TabButton>
                </div>

                <div className="p-6">

                    {/* OVERVIEW */}
                    {activeTab === 'overview' && (
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-3">
                                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Informazioni Progetto</h3>
                                    {[
                                        { icon: MapPinIcon, label: 'Luogo', value: `${project.location}${project.region ? `, ${project.region}` : ''}` },
                                        { icon: CalendarIcon, label: 'Inizio', value: formatDate(project.startDate) },
                                        { icon: CalendarIcon, label: 'Fine prevista', value: project.endDate ? formatDate(project.endDate) : 'Non definita' },
                                        { icon: UserIcon, label: 'Manager', value: project.manager ? `${project.manager.firstName} ${project.manager.lastName}` : 'Non assegnato' },
                                        { icon: UserIcon, label: 'Cliente', value: project.clientName || '—' },
                                    ].map(({ icon: Icon, label, value }) => (
                                        <div key={label} className="flex items-center gap-3">
                                            <div className="w-8 h-8 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0">
                                                <Icon className="w-4 h-4 text-gray-400" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-400">{label}</p>
                                                <p className="text-sm font-medium text-gray-900">{value}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="space-y-3">
                                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Finanziario</h3>
                                    {[
                                        { label: 'Budget stimato', value: formatCurrency(project.estimatedBudget), color: 'text-blue-600' },
                                        { label: 'Importo contratto', value: formatCurrency(project.contractAmount), color: 'text-purple-600' },
                                        { label: 'Spese effettive', value: formatCurrency(totalExpenses), color: utilization >= 90 ? 'text-red-600' : 'text-gray-900' },
                                        { label: 'Budget rimanente', value: formatCurrency(project.estimatedBudget - totalExpenses), color: 'text-emerald-600' },
                                        { label: 'Fondo contingenza', value: formatCurrency(project.contingencyFund || 0), color: 'text-gray-600' },
                                    ].map(({ label, value, color }) => (
                                        <div key={label} className="flex justify-between items-center py-2 border-b border-gray-50">
                                            <span className="text-sm text-gray-500">{label}</span>
                                            <span className={`text-sm font-semibold ${color}`}>{value}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* FASI E TASK */}
                    {activeTab === 'phases' && (
                        <div className="space-y-4">
                            {!project.phases?.length ? (
                                <p className="text-center text-gray-400 py-8">Nessuna fase definita</p>
                            ) : (
                                project.phases.map((phase) => (
                                    <div key={phase.id} className="border rounded-xl overflow-hidden">
                                        <div className="bg-gray-50 px-4 py-3 flex items-center justify-between">
                                            <div>
                                                <span className="font-semibold text-gray-900">{phase.name}</span>
                                                <span className="ml-2 text-xs text-gray-400">({phase.tasks?.length || 0} task)</span>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <span className="text-sm text-gray-500">{phase.progress || 0}%</span>
                                                <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                                    <div className="h-full bg-blue-500 rounded-full" style={{ width: `${phase.progress || 0}%` }} />
                                                </div>
                                            </div>
                                        </div>
                                        {phase.tasks && phase.tasks.length > 0 && (
                                            <div className="divide-y divide-gray-50">
                                                {phase.tasks.map((task) => (
                                                    <div key={task.id} className="px-4 py-3 flex items-center gap-3 hover:bg-gray-50">
                                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${TASK_STATUS_COLORS[task.status]}`}>
                                                            {TASK_STATUS_LABELS[task.status]}
                                                        </span>
                                                        <span className="text-sm text-gray-700 flex-1">{task.name}</span>
                                                        {task.assignedTo && (
                                                            <span className="text-xs text-gray-400 flex-shrink-0">
                                                                {task.assignedTo.firstName} {task.assignedTo.lastName}
                                                            </span>
                                                        )}
                                                        {task.dueDate && (
                                                            <span className="text-xs text-gray-400 flex-shrink-0">{formatDate(task.dueDate)}</span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    )}

                    {/* SPESE */}
                    {activeTab === 'expenses' && (
                        <div>
                            {!project.expenses?.length ? (
                                <p className="text-center text-gray-400 py-8">Nessuna spesa registrata</p>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full">
                                        <thead>
                                            <tr className="text-xs text-gray-400 uppercase tracking-wider border-b">
                                                <th className="py-3 text-left">Riferimento</th>
                                                <th className="py-3 text-left">Descrizione</th>
                                                <th className="py-3 text-left">Categoria</th>
                                                <th className="py-3 text-right">Importo</th>
                                                <th className="py-3 text-left">Data</th>
                                                <th className="py-3 text-center">Stato</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-50">
                                            {project.expenses.map((expense) => (
                                                <tr key={expense.id} className="hover:bg-gray-50 text-sm">
                                                    <td className="py-3 font-mono text-gray-600">{expense.reference}</td>
                                                    <td className="py-3 text-gray-700 max-w-xs truncate">{expense.description}</td>
                                                    <td className="py-3 text-gray-500">{CATEGORY_LABELS[expense.category] || expense.category}</td>
                                                    <td className="py-3 font-semibold text-gray-900 text-right">{formatCurrency(expense.totalAmount)}</td>
                                                    <td className="py-3 text-gray-500">{formatDate(expense.invoiceDate)}</td>
                                                    <td className="py-3 text-center">
                                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${expense.validated ? 'bg-emerald-100 text-emerald-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                                            {expense.validated ? '✓' : '⏳'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* DOCUMENTI */}
                    {activeTab === 'documents' && (
                        <div>
                            {!project.documents?.length ? (
                                <div className="text-center py-8">
                                    <DocumentTextIcon className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                                    <p className="text-gray-400">Nessun documento caricato</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {project.documents.map((doc) => (
                                        <a
                                            key={doc.id}
                                            href={doc.fileUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="flex items-center gap-3 p-3 border rounded-xl hover:border-blue-200 hover:bg-blue-50 transition group"
                                        >
                                            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600 text-lg group-hover:bg-blue-100">
                                                📄
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-900 truncate">{doc.name}</p>
                                                <p className="text-xs text-gray-400">{doc.type} · {Math.round((doc.fileSize || 0) / 1024)} KB</p>
                                            </div>
                                        </a>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};

export default ProjectDetail;